import { BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';

import { MARKETING_NAMES } from 'components/BusinessToolForm/Sections/MarketingAndSales/MarketingPlan/utils/form';
import {
  CHANNELS_NAMES,
  SALES_NAMES,
  STAGES_NAMES,
} from 'components/BusinessToolForm/Sections/MarketingAndSales/SalesPlanSection/utils/form';

const SALES_CHANNELS_INSTRUCTIONS = {
  [CHANNELS_NAMES.NAME]: {
    name: 'Canal de ventas',

    instruction:
      'Identifique los canales de venta más adecuados para su público objetivo. Incluya plataformas digitales, distribuidores, alianzas comerciales y estrategias de venta directa que maximicen el alcance y la captación de clientes.',

    exampleOne: 'Plataforma de comercio electrónico',
    exampleTwo: 'Alianzas con distribuidores',
  },

  [CHANNELS_NAMES.SHARE]: {
    name: 'Participación en las ventas',

    instruction:
      'Indique la contribución de cada canal al volumen total de ventas. Exprese la participación en porcentajes y asegúrese de que el total sea igual al 100 %.',

    exampleOne: '',
    exampleTwo: '',
  },

  [CHANNELS_NAMES.DESCRIPTION]: {
    name: 'Descripción del canal',

    instruction:
      'Describa el funcionamiento de cada canal de ventas. Explique cómo contribuye a ampliar la cobertura del mercado, facilitar el acceso de los clientes y apoyar la estrategia comercial.',

    exampleOne:
      'La tienda online permite a los clientes comprar en cualquier momento desde cualquier ubicación, ofreciendo pagos seguros y entrega a domicilio.',

    exampleTwo:
      'Las alianzas con distribuidores especializados en productos agrícolas permiten exhibir los productos en puntos de venta físicos, mejorar la disponibilidad local y ofrecer una experiencia de compra más cercana.',
  },
};

const SALES_PLAN_INSTRUCTIONS = {
  [SALES_NAMES.GOALS]: {
    name: 'Objetivos comerciales',

    instruction:
      'Defina los objetivos comerciales a corto y largo plazo. Establezca metas realistas con plazos concretos, teniendo en cuenta las tendencias del mercado, el entorno competitivo y las perspectivas de crecimiento.',

    exampleOne:
      'Objetivo a corto plazo: incrementar las ventas trimestrales un 10 % durante los próximos seis meses mediante la expansión a nuevos mercados regionales y una mayor interacción con los clientes. Objetivo a largo plazo: alcanzar un crecimiento del 20 % en las ventas anuales durante los próximos tres años gracias a la diversificación del portafolio y la expansión internacional.',

    exampleTwo:
      'Objetivo a corto plazo: aumentar un 20 % la captación de nuevos clientes en seis meses. Objetivo a largo plazo: incrementar un 50 % los ingresos procedentes de clientes recurrentes en los próximos dos años, adaptando continuamente la estrategia a la evolución del mercado.',
  },

  [SALES_NAMES.STAGES]: {
    [STAGES_NAMES.STAGE]: {
      name: 'Embudo de ventas',

      instruction:
        'Describa las etapas del proceso comercial, desde la generación de oportunidades hasta el cierre de la venta y la atención posterior. Destaque las actividades clave en cada fase para garantizar una experiencia de cliente sólida y sostenible.',

      exampleOne:
        'El proceso comercial comprende la generación de oportunidades, la cualificación de prospectos, la presentación de la solución, la negociación y el cierre. Tras la venta, el foco se centra en el seguimiento y la fidelización del cliente.',

      exampleTwo:
        'El proceso comienza con la captación de prospectos, continúa con su cualificación, la demostración del producto, la negociación y el cierre del acuerdo. Posteriormente se desarrollan acciones de soporte y fidelización para fortalecer la relación con el cliente.',
    },

    [STAGES_NAMES.DESCRIPTION]: {
      name: 'Descripción',

      instruction:
        'Describa la finalidad y las actividades principales de la etapa seleccionada dentro del proceso de ventas.',

      exampleOne:
        'En esta etapa se presenta la propuesta de valor a los prospectos cualificados, destacando las características, beneficios y ventajas competitivas de la solución para responder a sus necesidades específicas.',

      exampleTwo:
        'Esta fase consiste en evaluar el potencial de cada prospecto considerando sus necesidades, presupuesto y calendario de compra, recopilando la información necesaria para avanzar en el proceso comercial.',
    },
  },

  [SALES_NAMES.AFFECT_FACTORS]: {
    name: 'Factores del ciclo de ventas',

    instruction:
      'Describa los factores que influyen en la duración y evolución del ciclo comercial, como la estacionalidad, las condiciones económicas, las tendencias del mercado y la fase del ciclo de vida del producto. Explique cómo afectan a la demanda y a la planificación comercial.',

    exampleOne:
      'La demanda aumenta durante las temporadas de siembra y cosecha. La situación económica influye directamente en la capacidad de inversión de los clientes, mientras que la creciente orientación hacia la sostenibilidad impulsa la demanda de nuevas soluciones.',

    exampleTwo:
      'La demanda fluctúa según los ciclos de expansión empresarial, las tendencias del mercado global y la planificación presupuestaria de los clientes corporativos, factores que condicionan la velocidad del proceso comercial.',
  },

  [SALES_NAMES.PERFORMANCE_INDICATORS]: {
    name: 'Indicadores de rendimiento',

    instruction:
      'Defina los indicadores clave de rendimiento (KPI) para cada etapa del proceso comercial. Incluya métricas como la tasa de conversión, el valor medio de las oportunidades, la velocidad del ciclo de ventas y el coste de adquisición de clientes para medir el desempeño y optimizar los resultados.',

    exampleOne:
      'Durante la generación de oportunidades se controlan la tasa de conversión y el coste de adquisición de clientes. En la gestión del pipeline se monitorizan el valor medio de las oportunidades y la velocidad del ciclo de ventas. Tras el cierre se evalúan la satisfacción y la fidelización de los clientes.',

    exampleTwo:
      'Prospección: tasa de conversión de leads. Oportunidades: valor medio de la operación. Propuesta: tiempo medio de cierre. Venta cerrada: coste de adquisición de clientes (CAC).',
  },

  [SALES_NAMES.CHANNELS]: SALES_CHANNELS_INSTRUCTIONS,
  [SALES_NAMES.DETAILED_SALES_CHANNEL]: {
    name: 'Resumen de los canales de venta',

    instruction:
      'Describa la estrategia comercial y los canales de venta utilizados por la empresa. Incluya ventas directas, plataformas digitales, alianzas estratégicas, distribuidores y cualquier otro canal relevante, explicando cómo contribuyen a ampliar la cobertura del mercado y a alcanzar al público objetivo.',

    exampleOne:
      'La empresa comercializará sus productos mediante un equipo de ventas propio, una plataforma de comercio electrónico, alianzas estratégicas con empresas del sector y una amplia red de distribuidores. La combinación de estos canales permitirá maximizar la cobertura del mercado y fortalecer la relación con los clientes.',

    exampleTwo:
      'La empresa combinará ventas directas a través de su sitio web con campañas de correo electrónico segmentadas. Además, establecerá alianzas con proveedores de servicios lingüísticos y desarrollará una red de distribución en mercados estratégicos. Las redes sociales y los medios especializados reforzarán la visibilidad y la captación de clientes.',
  },

  [SALES_NAMES.SALES_TEAM_STRUCTURE]: {
    name: 'Equipo comercial',

    instruction:
      'Presente la estructura del equipo responsable de las ventas. Describa los principales cargos, sus responsabilidades y las relaciones jerárquicas dentro del área comercial. Destaque cómo la organización del equipo contribuye al logro de los objetivos de ventas.',

    exampleOne:
      'El Director Comercial define la estrategia de ventas y supervisa el rendimiento del equipo. Los gerentes comerciales coordinan las operaciones diarias, apoyan a los ejecutivos de ventas y garantizan el cumplimiento de los objetivos establecidos.',

    exampleTwo:
      'La empresa cuenta con una estructura comercial organizada por regiones. Cada zona está dirigida por un gerente responsable de la captación y fidelización de clientes en su territorio, mientras que el Director Comercial coordina la estrategia global y la toma de decisiones.',
  },

  [SALES_NAMES.MOTIVATION_SYSTEM]: {
    name: 'Sistema de compensación e incentivos',

    instruction:
      'Describa el modelo de compensación del equipo comercial. Explique la estructura salarial, las comisiones, los incentivos y las bonificaciones vinculadas al cumplimiento de objetivos, la satisfacción de los clientes y el crecimiento del negocio.',

    exampleOne:
      'El equipo comercial recibe un salario fijo complementado con comisiones variables en función del volumen de ventas, la satisfacción del cliente y el crecimiento de la cuota de mercado. Además, se ofrecen bonificaciones, programas de reconocimiento, formación especializada e incentivos para los mejores resultados.',

    exampleTwo:
      'La remuneración combina un salario base con comisiones por objetivos alcanzados y bonificaciones por rendimiento. El desempeño se evalúa periódicamente mediante indicadores clave que garantizan la alineación entre los resultados individuales y los objetivos de la empresa.',
  },
};
const MARKETING_PLAN_INSTRUCTIONS = {
  [MARKETING_NAMES.ADVERTISING_CAMPAIGNS]: {
    name: 'Publicidad y promoción',

    instruction:
      'Describa los canales y las acciones de marketing que utilizará para captar clientes potenciales. Incluya campañas digitales, alianzas estratégicas, colaboraciones, acciones de networking y otras iniciativas orientadas a generar notoriedad de marca y convertir el interés en oportunidades de negocio. Explique cómo estas acciones acompañan al cliente a lo largo de todo el embudo de conversión.',

    exampleOne:
      'La estrategia de promoción combinará redes sociales, campañas de publicidad digital segmentadas, medios especializados y alianzas estratégicas con empresas del sector. Las colaboraciones con socios y líderes de opinión reforzarán la notoriedad de la marca y facilitarán la captación de nuevos clientes durante todo el proceso de conversión.',

    exampleTwo:
      'La empresa desarrollará campañas segmentadas en Google Ads y redes sociales, complementadas con marketing de contenidos, newsletters y colaboraciones con asociaciones profesionales. Estas acciones incrementarán la visibilidad de la marca y nutrirán a los prospectos hasta su conversión en clientes.',
  },

  [MARKETING_NAMES.BUDGET]: {
    name: 'Presupuesto de marketing y publicidad',

    instruction:
      'Detalle la distribución del presupuesto de marketing entre los distintos canales y actividades. Especifique la inversión prevista para marketing digital, medios tradicionales, eventos y otras iniciativas, justificando la asignación en función del alcance esperado, la visibilidad de la marca y el retorno de la inversión (ROI).',

    exampleOne: `Presupuesto total: 500.000 USD

Marketing digital: 40 %
Publicidad impresa: 20 %
Eventos y ferias: 30 %
Otros: 10 %

La distribución prioriza los canales con mayor impacto en notoriedad de marca, generación de demanda y retorno de la inversión.`,

    exampleTwo: `Presupuesto total: 300.000 USD

Marketing digital: 60 %
Publicidad impresa: 20 %
Eventos: 20 %

La inversión se distribuye para maximizar la cobertura del mercado y optimizar el retorno de la inversión en cada canal.`,
  },

  [MARKETING_NAMES.BRAND_ELEMENTS]: {
    name: 'Identidad de marca',

    instruction:
      'Describa los principales elementos que definen la identidad de la marca. Incluya el logotipo, el eslogan, la identidad visual, la paleta de colores y otros elementos distintivos. Explique cómo estos componentes reflejan los valores de la empresa y fortalecen el reconocimiento de la marca entre el público objetivo.',

    exampleOne:
      'El logotipo representa innovación, solidez y eficiencia mediante un símbolo inspirado en un tractor de orugas. El eslogan "Impulsando la agricultura del futuro" transmite el compromiso de la empresa con la productividad y la sostenibilidad. La combinación de tonos verdes y azules refuerza la asociación con la innovación, la naturaleza y la confianza.',

    exampleTwo:
      'La identidad visual combina una tipografía contemporánea con un icono que simboliza la conectividad global. El eslogan "Conectando culturas, generando confianza" refleja el compromiso de la empresa con una comunicación internacional fluida. La paleta de colores en tonos azules y verdes transmite confianza, innovación y profesionalidad.',
  },
  [MARKETING_NAMES.POSITIONING]: {
    name: 'Posicionamiento',

    instruction:
      'Describa el posicionamiento de la empresa en el mercado. Explique cómo desea que la marca sea percibida por sus clientes y qué elementos la diferencian de la competencia. Destaque aspectos como el reconocimiento de marca, la propuesta de valor y la influencia de la empresa dentro de su sector.',

    exampleOne:
      'La empresa se posiciona como un referente en soluciones innovadoras para el sector agrícola. Su propuesta de valor combina alto rendimiento, sostenibilidad y fiabilidad, ofreciendo maquinaria de última generación con un impacto mínimo sobre el suelo.',

    exampleTwo:
      'La empresa aspira a convertirse en la plataforma de referencia para servicios lingüísticos, asociando su marca con innovación, confianza y facilidad de uso. Su objetivo es ser el socio preferente para organizaciones que requieren una comunicación multilingüe rápida, precisa y segura.',
  },

  [MARKETING_NAMES.PRICING_STRATEGY]: {
    name: 'Estrategia de precios',

    instruction:
      'Describa la política de precios de la empresa. Explique el modelo utilizado —como fijación basada en el valor, en los costes, en la competencia o una combinación de varios enfoques— e indique cómo esta estrategia contribuye al posicionamiento de la empresa y a su rentabilidad.',

    exampleOne:
      'La empresa aplica una estrategia de precios basada en el valor, respaldada por la calidad superior y el rendimiento de sus productos. La combinación de precios competitivos y servicios de valor añadido, como programas de mantenimiento, fortalece su posicionamiento premium y maximiza el retorno para el cliente.',

    exampleTwo:
      'La empresa utiliza un modelo de precios dinámicos que adapta las tarifas según la demanda, la disponibilidad de especialistas y la complejidad de cada proyecto. Este enfoque permite mantener la competitividad y ofrecer soluciones ajustadas a las necesidades de cada cliente.',
  },

  [MARKETING_NAMES.TOOLS_AND_TECHNOLOGIES]: {
    name: 'Gestión de las relaciones con los clientes (CRM)',

    instruction:
      'Describa las herramientas y tecnologías utilizadas para gestionar la relación con los clientes. Incluya los canales de comunicación disponibles, como teléfono, correo electrónico, chat, redes sociales y otros medios de atención. Explique cómo soluciones como los sistemas CRM, la automatización y los asistentes virtuales contribuyen a mejorar la experiencia del cliente y la eficiencia operativa.',

    exampleOne:
      'La empresa utilizará Salesforce como plataforma central de gestión de clientes. Los clientes podrán contactar con el equipo a través de teléfono, correo electrónico, chat en línea y redes sociales. La incorporación de asistentes virtuales agilizará la gestión de consultas y mejorará la calidad del servicio.',

    exampleTwo:
      'La empresa empleará un sistema CRM para centralizar la información de clientes, gestionar oportunidades comerciales y analizar datos. Estas herramientas permitirán personalizar la comunicación, optimizar el seguimiento comercial y aumentar la satisfacción y fidelización de los clientes.',
  },
};

export const MARKETING_AND_SALES_INSTRUCTIONS = {
  [BUSINESS_TOOL_SUB_SECTIONS.MARKETING_AND_SALES.SALES_PLAN]: SALES_PLAN_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.MARKETING_AND_SALES.MARKETING_PLAN]: MARKETING_PLAN_INSTRUCTIONS,
};
